/**
 * Represents a primitive‐level difference between two values.
 */
export interface PrimitiveDiff {
    before: unknown;
    after: unknown;
}

/**
 * Represents an array of diffs (each entry is either a nested DiffNode or undefined if no change at that index).
 */
export type ArrayDiff = Array<DiffNode | undefined>;

/**
 * Represents an object whose properties each map to a nested DiffNode.
 */
export interface ObjectDiff {
    [key: string]: DiffNode;
}

/**
 * A DiffNode can be either:
 * - a PrimitiveDiff (for primitive or type‐mismatch differences),
 * - an ArrayDiff (for array comparisons),
 * - or an ObjectDiff (for plain‐object comparisons).
 */
export type DiffNode = PrimitiveDiff | ArrayDiff | ObjectDiff;

/**
 * Deeply compares two values (objects, arrays, primitives, Dates, RegExps)
 * and returns a nested DiffNode describing differences, or `undefined` if no diff.
 */
export function deepDiff(a: unknown, b: unknown): DiffNode | undefined {
    // Same reference or both NaN → no diff
    if (
        a === b ||
        (typeof a === 'number' &&
            typeof b === 'number' &&
            Number.isNaN(a) &&
            Number.isNaN(b))
    ) {
        return undefined;
    }

    // Types differ or one is null/undefined
    if (typeof a !== typeof b || a === null || b === null) {
        return { before: a, after: b };
    }

    // Handle Date objects
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime()
            ? undefined
            : { before: a, after: b };
    }

    // Handle RegExp objects
    if (a instanceof RegExp && b instanceof RegExp) {
        return a.source === b.source && a.flags === b.flags
            ? undefined
            : { before: a, after: b };
    }

    // Primitives (string, number, boolean, symbol) or functions
    if (typeof a !== 'object') {
        return a === b ? undefined : { before: a, after: b };
    }

    // Arrays
    if (Array.isArray(a)) {
        if (!Array.isArray(b)) {
            return { before: a, after: b };
        }
        const arrA = a as Array<unknown>;
        const arrB = b as Array<unknown>;
        const maxLen = Math.max(arrA.length, arrB.length);
        const diffArr: ArrayDiff = [];
        let hasDiff = false;

        for (let i = 0; i < maxLen; i++) {
            const childDiff = deepDiff(arrA[i], arrB[i]);
            diffArr[i] = childDiff;
            if (childDiff !== undefined) {
                hasDiff = true;
            }
        }

        return hasDiff ? diffArr : undefined;
    }

    // Plain Objects
    const objA = a as Record<string, unknown>;
    const objB = b as Record<string, unknown>;
    const keysA = Object.keys(objA);
    const keysB = Object.keys(objB);
    const allKeys = Array.from(new Set([...keysA, ...keysB]));
    const diffObj: ObjectDiff = {};
    let foundDiff = false;

    for (const key of allKeys) {
        const hasA = Object.prototype.hasOwnProperty.call(objA, key);
        const hasB = Object.prototype.hasOwnProperty.call(objB, key);

        if (!hasB) {
            diffObj[key] = { before: objA[key], after: undefined };
            foundDiff = true;
        } else if (!hasA) {
            diffObj[key] = { before: undefined, after: objB[key] };
            foundDiff = true;
        } else {
            const childDiff = deepDiff(objA[key], objB[key]);
            if (childDiff !== undefined) {
                diffObj[key] = childDiff;
                foundDiff = true;
            }
        }
    }

    return foundDiff ? diffObj : undefined;
}
